import React, { useState, useEffect, useRef } from "react";
import { updateItem, updateItems, addToCart } from "frontend/js/useShopifyCart";

import ImageComponent from "@project-stories/elements/Image/Image";

import { TextField } from "@arctheme-components/elements/TextField/TextField";
import { Paragraph } from "@arctheme-components/elements/Paragraph/Paragraph";
import LoadingSpinner from "@project-stories/elements/Spinner/LoadingSpinner";
import shopify from "../../../../js/shopify";
import ClickAndCollect from "../StoreFinder/ClickAndCollect";
import InventoryDetail from "./InventoryDetail";
import MinicartRecommendations from "./MinicartRecommendations";
import "./Minicart.scss";
import { mobileBreakpoint } from "frontend/entrypoints/theme";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";

interface Store {
  id: string;
  name: string;
  shopify_location_id?: string;
  clickandcollect_buffer?: number;
  sfs_buffer?: number;
}

// C&C Store persistence
const CC_STORE_KEY = 'mjbale_cc_selected_store';

// Utility functions for localStorage with error handling
const saveSelectedStore = (store: Store | null) => {
  try {
    if (store) {
      localStorage.setItem(CC_STORE_KEY, JSON.stringify(store));
    } else {
      localStorage.removeItem(CC_STORE_KEY);
    }
  } catch (error) {
    console.warn('Failed to save C&C store to localStorage:', error);
  }
};

const loadSelectedStore = (): Store | null => {
  try {
    const stored = localStorage.getItem(CC_STORE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Failed to load C&C store from localStorage:', error);
    // Clear corrupted data
    try {
      localStorage.removeItem(CC_STORE_KEY);
    } catch {}
    return null;
  }
};

const Minicart = ({ cartData, style = "drawer", settings, props }) => {
  const [cart, setCart] = useState({
    items: cartData.items,
    item_count: cartData.item_count,
    items_subtotal_price: cartData.items_subtotal_price,
  });
  
  const [selectedStore, setSelectedStore] = useState(() => loadSelectedStore());
  const [activeTab, setActiveTab] = useState(() => {
    const persistedStore = loadSelectedStore();
    return persistedStore ? "clickAndCollect" : "homeDelivery";
  });
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [storeResults, setStoreResults] = useState([]);
  const [newlyAddedKey, setNewlyAddedKey] = useState<string | null>(null);
  const prevItemKeysRef = useRef<Set<string>>(new Set());
  const isCartSeededRef = useRef(false);
  const isMobile = useMobileBreakpoint(mobileBreakpoint);
  const recommendationEnabled = props.recommendation.enabled || false;

  const allVariantsAvailable =
    activeTab === "homeDelivery"
      ? true
      : activeTab !== "clickAndCollect" || !selectedStore
      ? false
      : (() => {
          const variantsAtStore =
            storeResults?.storesByLocation?.[selectedStore.shopify_location_id];

          // If we don't yet have availability data for this store (e.g. after a refresh),
          // don't block checkout – assume variants are available until proven otherwise.
          if (!variantsAtStore) {
            return true;
          }

          return cart.items.every((item) => {
            return variantsAtStore.some((variant) => {
              if (String(variant.shopify_variant_id) !== String(item.variant_id)) {
                return false;
              }
              const requiredStock =
                (selectedStore as Store).clickandcollect_buffer === 1 ? 2 : 1;
              return (variant.free_stock || 0) >= requiredStock;
            });
          });
        })();

  const handleRemoveStore = () => {
    setSelectedStore(null);
  };

  // Persist selectedStore to localStorage whenever it changes
  useEffect(() => {
    saveSelectedStore(selectedStore);
  }, [selectedStore]);

  useEffect(() => {
    // Function to fetch the cart data
    const fetchCart = async (event) => {
      window.dispatchEvent(
        new CustomEvent("show-spinner", {
          detail: {
            id: "cart-spinner",
          },
        })
      );

      try {
        const response = await fetch(`/cart.js`);
        const cartData = await response.json();
        const incoming = cartData.items || [];

        if (isCartSeededRef.current) {
          const newItem = incoming.find(
            (item) => !prevItemKeysRef.current.has(item.key)
          );
          if (newItem) {
            setNewlyAddedKey(newItem.key);
          }
        }
        
        prevItemKeysRef.current = new Set(incoming.map((i) => i.key));
        isCartSeededRef.current = true;
        setCart({ ...cartData }); // Force re-render
      } catch (error) {
        console.error("Error fetching cart:", error);
      }
      window.dispatchEvent(
        new CustomEvent("hide-spinner", {
          detail: {
            id: "cart-spinner",
          },
        })
      );
    };

    // Fetch cart data initially
    fetchCart({ detail: {} });

    // Set up an observer for cart changes using the Liquid Ajax Cart JS library
    document.addEventListener("liquid-ajax-cart:request-end", fetchCart);
    document.addEventListener("fetch-api-cart:request-end", fetchCart);
    document.addEventListener("minicart:cart-updated", fetchCart);
  
    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener("liquid-ajax-cart:request-end", fetchCart);
      document.removeEventListener("fetch-api-cart:request-end", fetchCart);
      document.removeEventListener("minicart:cart-updated", fetchCart);
    };
  }, []);

  return (
    <>
      <div x-show="$store.global.isMinicartVisible">
        <div
          className="fixed inset-0 z-[49] bg-[rgba(114,117,126,0.7)]"
          // @ts-ignore
          onClick={() => window.Alpine?.store("global").toggleMinicart()}
          onMouseDown={(e) => e.preventDefault()}
          onMouseUp={(e) => e.stopPropagation()}
        />
      </div>
      <div
        className={`minicart-wrapper flex ${style} relative z-[50] pointer-events-auto`}
        x-cloak="true"
        x-transition:enter="transition ease-out duration-300"
        x-transition:enter-start="off-screen-right"
        x-transition:enter-end="right-50"
        x-transition:leave="transition ease-in duration-300"
        x-transition:leave-start="right-50"
        x-transition:leave-end="off-screen-right"
        x-show="$store.global.isMinicartVisible"
      >
        {recommendationEnabled && !isMobile && (
          <MinicartRecommendations
            className="hidden lg:block lg:sticky lg:top-0 lg:self-start"
            cartItems={cart.items}
            settings={settings}
            globals={props.globals}
            manualProducts={props.recommendation.products_manual}
            collectionProducts={props.recommendation.products_collection}
          />
        )}
        <div className={"minicart relative w-full md:max-w-[474px]"}>
          <LoadingSpinner id={"cart-spinner"} />
          <div className="flex-none">
            <CartTitle settings={props} />

            {props.globals.is_au &&
              <div className="flex justify-around">
                <button
                  className={`bc text-[#99A8B2] p-0 !h-[57px] border-b-2 lg:px-10 lg:flex-none ${activeTab === "homeDelivery" ? "active text-black border-[#54565A]" : "border-transparent"}`}
                  onClick={() => setActiveTab("homeDelivery")}
                >
                  <i className="text-[22px] icon icon-truck"></i>
                  Home Delivery
                </button>

                  <button
                    className={`bc text-[#99A8B2] p-0 !h-[57px] border-b-2 lg:px-10 lg:flex-none ${activeTab === "clickAndCollect" ? "active text-black border-[#54565A]" : "border-transparent"}`}
                    onClick={() => setActiveTab("clickAndCollect")}
                  >
                    <i className="text-[18px] icon icon-shipping"></i>
                    Click & Collect
                  </button>
              </div>
            }
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="relative grid items-center w-full grid-cols-2 bg-[#F5F5F5] p-6 gap-x-2">
              <CartCount item_count={cart.item_count} />
              {activeTab === "clickAndCollect" && (
                <>
                  <div className="flex items-center justify-between b2 gap-x-[8px] flex-row lg:flex-row">
                    <div className="flex flex-col items-center flex-1 lg:flex-row lg:gap-x-2">
                      <span className={"b-characteristics text-[#99A8B2]"}>Store:</span>
                      {selectedStore ? (
                        <>
                          <span
                            className="underline text-sm/[18px] normal-case cursor-pointer text-[#00263E] font-normal lg:text-base/4"
                            onClick={() => setIsStoreModalOpen(true)}
                          >
                            {selectedStore.name}
                          </span>
                        </>
                      ) : (
                        <button
                          className="underline text-sm/[18px] text-[#00263E] font-normal p-0 min-h-[auto] h-auto normal-case tracking-normal lg:text-base/4 "
                          onClick={() => setIsStoreModalOpen(!isStoreModalOpen)}
                        >
                          Select a store
                        </button>
                      )}
                    </div>
                    {selectedStore && (
                      <button
                        className="flex items-center justify-center w-6 h-6 p-0 min-h-max"
                        onClick={handleRemoveStore}
                      >
                        <i className="text-2xl text-black icon icon-cross-circle"></i>
                      </button>
                    )}
                  </div>

                  {isStoreModalOpen && (
                    <ClickAndCollect
                      isOpen={isStoreModalOpen}
                      onClose={() => setIsStoreModalOpen(false)}
                      onSelectStore={(store) => setSelectedStore(store)}
                      selectedStore={selectedStore}
                      displayMode="status"
                      variantIds={cart.items.map(item => item.variant_id)}
                      cartCount={cart.item_count}
                      onStoreResultsFetched={setStoreResults}
                    />
                  )}
                </>
              )}
            </div>

            {activeTab === "homeDelivery" && (
              <>
                <CartItems
                  tab="homeDelivery"
                  items={cart.items}
                  show_vendor={false}
                  show_total_price={settings.show_total_price}
                  selectedStore={selectedStore}
                  storeResults={storeResults}
                  props={props}
                  newlyAddedKey={newlyAddedKey}
                  onAnimationComplete={() => setNewlyAddedKey(null)}
                />

                {cart.items.length <= 0 && (
                  <div
                    className={
                      "flex flex-col gap-y-[16px] text-center h-full justify-center p-[16px]"
                    }
                  >
                    <div className={"b2 text-[#54565A]"}>
                      {settings.empty_text_one}
                      <br />
                      {settings.empty_text_two}
                    </div>
                    <a
                      x-on:click="$store.global.toggleMinicart()"
                      className={"minicart-empty__button button btn-outline"}
                    >
                      {settings.empty_button}
                    </a>
                  </div>
                )}
              </>
            )}
            {activeTab === "clickAndCollect" && (
              <>
                <CartItems
                  tab="clickAndCollect"
                  items={cart.items}
                  show_vendor={false}
                  show_total_price={settings.show_total_price}
                  selectedStore={selectedStore}
                  storeResults={storeResults}
                  newlyAddedKey={newlyAddedKey}
                  onAnimationComplete={() => setNewlyAddedKey(null)}
                />

                {cart.items.length <= 0 && (
                  <div
                    className={
                      "flex flex-col gap-y-[16px] text-center h-full justify-center p-[16px]"
                    }
                  >
                    <div className={"b2 text-[#54565A]"}>
                      {settings.empty_text_one}
                      <br />
                      {settings.empty_text_two}
                    </div>
                    <a
                      x-on:click="$store.global.toggleMinicart()"
                      className={"minicart-empty__button button btn-outline"}
                    >
                      {settings.empty_button}
                    </a>
                  </div>
                )}
              </>
            )}
            {recommendationEnabled && isMobile && (
              <MinicartRecommendations
                className="lg:hidden"
                cartItems={cart.items}
                settings={settings}
                globals={props.globals}
                manualProducts={props.recommendation.products_manual}
                collectionProducts={props.recommendation.products_collection}
              />
            )}
          </div>
          
          {activeTab === "clickAndCollect" && selectedStore && (
            <div className="bg-[#FFF5E5] text-[#8A6D3B] text-sm px-4 py-3 lg:px-8">
              {settings.click_and_collect_message}
            </div>
          )}

          {cart.items.length > 0 && (
            <div
              className={
                "flex-none bg-white pb-6 border-t border-[#ECEBEB] px-4 lg:px-8"
              }
            >
              <CartTotals
                cart_subtotal={cart.total_price / 100}
                settings={settings}
              />
              {/*<CartNotes show={true}/>*/}
              <MiniCartActions show_cart={false} allVariantsAvailable={allVariantsAvailable} selectedStore={selectedStore} storeResults={storeResults} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const CartTitle = ({ settings }) => {
  const { globals } = settings;
  return (
    <div className="title-row py-[17px] px-4 border-[#ECEBEB] border-b lg:px-6 lg:py-5">
      <div className="b1 text-[#BBBBBD]" id="minicart-label">
        My Shopping Bag
      </div>

      <div className="flex gap-[24px]">
        <a href="/account" className="antialiased [&>svg]:h-[28px]">
          {!globals.is_customer_logged_in && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.26005 19.5H19.74C19.909 19.5006 20.0668 19.4158 20.1596 19.2746C20.2524 19.1334 20.2676 18.9549 20.2 18.8L19.5 16.2C18.9121 14.0163 16.9315 12.4991 14.67 12.5H9.33005C7.06856 12.4991 5.08803 14.0163 4.50005 16.2L3.80005 18.8C3.73246 18.9549 3.74766 19.1334 3.84045 19.2746C3.93324 19.4158 4.09108 19.5006 4.26005 19.5Z"
                stroke="currentColor"
                strokeLinecap="round"
              />
              <circle
                cx="12"
                cy="7"
                r="3.5"
                stroke="currentColor"
                strokeLinecap="round"
              />
            </svg>
          )}
          {globals.is_customer_logged_in && (
            <svg
              id="Layer_2"
              data-name="Layer 2"
              xmlns="http://www.w3.org/2000/svg"
              version="1.1"
              viewBox="320 308 440 600"
            >
              <path
                className="cls-1"
                d="M766.3,723.2c-4.5-17.5-9.3-35-14-52.5-19-72-67.7-114.6-138.1-117.2-50.8-1.9-101.8-1.6-152.7.1-58.3,2-108.8,41.3-127.1,96.7-7.9,24.1-14,48.8-20.8,73.3-6.9,24.5,4,38.7,29.6,38.7,65.4,0,130.9,0,196.3,0s130.9,0,196.3,0c26.9,0,37-13.1,30.4-39.2Z"
              />
              <path
                className="cls-1"
                d="M540.6,526.5c58.3-.3,104.4-47.1,103.9-105.4-.5-57.8-46.6-103.5-104.4-103.6-58.3,0-104.7,46.4-104.6,104.7.1,58.3,46.8,104.6,105.1,104.3Z"
              />
            </svg>
          )}
        </a>

        <a
          className="cursor-pointer"
          x-on:click="$store.global.toggleMinicart()"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M19.82 4.17999C19.58 3.94 19.1912 3.94 18.9513 4.17999L12 11.1313L5.04874 4.17999C4.80875 3.94 4.41996 3.94 4.17999 4.17999C3.94002 4.41998 3.94 4.80877 4.17999 5.04874L11.1313 12L4.17999 18.9513C3.94 19.1912 3.94 19.58 4.17999 19.82C4.41998 20.06 4.80876 20.06 5.04874 19.82L12 12.8687L18.9513 19.82C19.1912 20.06 19.58 20.06 19.82 19.82C20.06 19.58 20.06 19.1912 19.82 18.9513L12.8687 12L19.82 5.04874C20.06 4.80875 20.06 4.41996 19.82 4.17999Z"
              fill="#99A8B2"
            />
          </svg>
        </a>
      </div>
    </div>
  );
};

const CartCount = ({ item_count }) => {
  return (
    <div className="item-count-message b2 bg-[#F5F5F5] flex gap-x-[8px]">
      <span className={"b-characteristics text-[#99A8B2]"}>Total Items</span>
      <span className="b-characteristics text-[#00263E]">{item_count}</span>
    </div>
  );
};

const createKeyValuePairs = (tags) => {
  const tagsArray = Array.isArray(tags) ? tags : [tags];

  return tagsArray.reduce((acc, tag) => {
    const [key, value] = tag.split(":");
    if (value) {
      if (acc[key]) {
        if (Array.isArray(acc[key])) {
          acc[key].push(value);
        } else {
          acc[key] = [acc[key], value];
        }
      } else {
        acc[key] = value;
      }
    }
    return acc;
  }, {});
};

const AddedToCartPlaceholder = ({ phase }: { phase: 'placeholder' | 'sliding' }) => {
  const isSliding = phase === 'sliding';
  return (
    <div
      className="absolute inset-0 z-10 flex items-center justify-center bg-[#F5F5F5] backdrop-blur-[10px]"
      style={{
        transform: isSliding ? 'translateX(100%)' : 'translateX(0)',
        transition: isSliding ? 'transform 0.4s cubic-bezier(0.4,0,0.2,1)' : 'none',
        pointerEvents: 'none',
      }}
    >
      <span
        className="blabel text-[#99A8B2] uppercase"
      >
        Added to Cart
      </span>
    </div>
  );
};

const CartItems = ({ items, show_vendor = false, show_total_price = true, tab, selectedStore, storeResults, props = {} as any, newlyAddedKey = null, onAnimationComplete = () => {} }: any) => {
  const AjaxCartQuantity = "ajax-cart-quantity" as keyof JSX.IntrinsicElements;
  const [products, setProducts] = useState([]);
  // @ts-ignore
  const currencySymbol = window.Alpine?.store("currency").getCurrencySymbol();

  // Animation state: 'placeholder' → show placeholder, 'sliding' → slide item in / placeholder out
  const [animKey, setAnimKey] = useState<string | null>(null);
  const [animPhase, setAnimPhase] = useState<'placeholder' | 'sliding' | null>(null);

  useEffect(() => {
    if (!newlyAddedKey) return;
    setAnimKey(newlyAddedKey);
    setAnimPhase('placeholder');
    // After 300ms show the real item sliding in from the left
    const t1 = setTimeout(() => setAnimPhase('sliding'), 300);
    // After the slide transition completes (~400ms), clean up
    const t2 = setTimeout(() => {
      setAnimKey(null);
      setAnimPhase(null);
      onAnimationComplete();
    }, 800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [newlyAddedKey]);

  // Quantity update handler using Shopify cart module
  const handleQuantityChange = async (lineItemKey: string, newQty: number) => {
    try {
      await updateItem({ id: lineItemKey, quantity: newQty });
    } catch (error) {
      console.error("Failed to update item quantity:", error);
    }
  };

  useEffect(() => {
    if (items && items.length > 0) {
      const productIdsList = [
        ...new Set(items.map((product) => product.product_id)),
      ];

      async function fetchProducts(productIds) {
        const products = await shopify.getShopifyProductList(productIds);
        setProducts(products);
      }

      fetchProducts(productIdsList);
    }
  }, [items]);

  return (
    <div className={"relative"}>
      {items.length > 0 && (
        <ul id="items" className="items-list px-[16px] flex flex-col flex-grow">
          {items.map((line_item, index) => {
            const line_item_price = line_item.line_price / 100;
            const line_item_quantity = line_item.quantity;
            const indexLineItem = index + 1;
            const size = line_item.options_with_values.find(
              (option) => option.name.toLowerCase() === "size"
            );
            const colour = line_item.options_with_values.find(
              (option) => option.name.toLowerCase() === "color"
            );
            const value = line_item.options_with_values.find(
              (option) => option.name.toLowerCase() === "value"
            );
            const properties = line_item.properties
              ? Object.entries(line_item.properties).map(([name, value]) => ({ name, value }))
              : [];
            if (products) {
              products.forEach((product) => {
                if (line_item.product_id === product.id) {
                  line_item.product = product;
                }
              });
            }

            let totalDiscount = 0;
            if (line_item.discounts) {
              line_item.discounts.forEach((discount) => {
                totalDiscount += discount.amount;
              });
            }
            const isAnimating = animKey === line_item.key;
            const classItemAnimating = isAnimating ? (animPhase === 'sliding' ? 'item-animating-sliding' : 'item-animating-placeholder') : '';

            return (
              <React.Fragment key={`${tab}_${line_item.id}_${index}`}>
                <li
                  data-id={indexLineItem}
                  id={`${line_item.id}`}
                  data-line={indexLineItem}
                  className={`item item-animating-sliding
                    ${isAnimating ? `relative overflow-hidden ${classItemAnimating}` : ''}`}
                >
                  { index === 0 && isAnimating && (
                    <AddedToCartPlaceholder phase={animPhase} />
                  )}
                  <a href={"/products/" + line_item.handle}>
                    <ImageComponent
                      imageUrl={line_item.image}
                      alt={line_item.product_title}
                      isLazy={false}
                      classes={"item__image"}
                    />
                  </a>

                  <div className={`item-details flex-auto`}>
                    <div>
                      <div className="uppercase item-details__title bc">
                        <span className={`item-details__title-product`}>
                          {line_item.product_title}
                        </span>
                        &nbsp;
                        {colour && (
                          <span className="item-details__title-colour">
                            {colour.value}
                          </span>
                        )}
                      </div>

                      {show_vendor && (
                        <small className={`item-vendor`}>
                          {line_item.vendor}
                        </small>
                      )}

                      <div className={`item-options`}>
                        {line_item.product_type?.toLowerCase() === "gift card" && (
                          <p className="option" data-option-name="Value">
                            <span className="b3 text-[#99A8B2] capitalize">Value: </span>
                            <span className="b3">
                              {line_item.options?.[0] || line_item.variant_title}
                            </span>
                          </p>
                        )}
                        {size && (
                          <p className={`option`} data-option-name={size.name}>
                            <span className={"b3 text-[#99A8B2]"}>
                              {size.name}:{" "}
                            </span>
                            <span className={"b3"}>{size.value}</span>
                          </p>
                        )}
                        {value && line_item.product_type?.toLowerCase() !== "gift card" && (
                          <p className={`option`} data-option-name={value.name}>
                            <span className={"b3 text-[#99A8B2] capitalize"}>
                              {value.name}:{" "}
                            </span>
                            <span className={"b3"}>{value.value}</span>
                          </p>
                        )}
                        {properties &&
                          properties.map((property, index) => {
                            if (
                              property.name === "_suitKit" ||
                              property.name === "__shopify_send_gift_card_to_recipient" ||
                              property.name === "_omneo_discount_key" ||
                              property.name === "__gc_unique_id"
                            )
                              return null;
                            return (
                              <p
                                key={`${property.name}_${index}`}
                                className={`option`}
                                data-option-name={property.name}
                              >
                                <span className={"b3 text-[#99A8B2] capitalize"}>
                                  {property.name}:{" "}
                                </span>
                                <span className={"b3 break-all"}>
                                  {property.value}
                                </span>
                              </p>
                            );
                          })}
                      </div>

                      <div className={"discounts_applied"}>
                        {line_item.discounts.map((discount, index) => {
                          // if (discount.title === "BALE BUCKS APPLIED") return null;
                          if (discount.title.trim().toLowerCase() === "bale bucks applied".toLowerCase()) return null;
                          return (
                            <p
                              key={`${discount.title}_${index}`}
                              className="b3 text-[14px] font-[400] text-[#D12727]"
                            >
                              {discount.title}
                            </p>
                          );
                        })}
                      </div>

                      <div className={`flex items-center quantity-selector gap-[16px]`}>
                        {line_item.product_type?.toLowerCase() !== "gift card" && (
                          line_item.properties && line_item.properties._suitKit ? (
                            <div className="flex items-center w-full max-w-[108px] h-[40px] bg-[#F5F5F5]">
                              <button
                                type="button"
                                className="w-auto h-auto px-3"
                                onClick={() =>
                                  handleQuantityChange(line_item.key, line_item_quantity - 1)
                                }
                                disabled={line_item_quantity <= 1}
                              >
                                <span className="icon-minus text-[14px] text-[#99A8B2]"></span>
                              </button>
                              <input
                                type="number"
                                className="quantity-control-input b3"
                                value={line_item_quantity}
                                readOnly
                              />
                              <button
                                type="button"
                                className="h-auto px-3"
                                onClick={() =>
                                  handleQuantityChange(line_item.key, line_item_quantity + 1)
                                }
                              >
                                <span className="icon-plus text-[14px] text-[#99A8B2]"></span>
                              </button>
                            </div>
                          ) : (
                            <AjaxCartQuantity>
                              <div className="flex items-center w-full max-w-[108px] h-[40px] bg-[#F5F5F5]">
                                <button
                                  data-ajax-cart-quantity-minus
                                  className="w-auto h-auto px-3"
                                >
                                  <span className="icon-minus text-[14px] text-[#99A8B2]"></span>
                                </button>
                                <input
                                  data-ajax-cart-quantity-input={`${indexLineItem}`}
                                  value={line_item_quantity}
                                  readOnly
                                  type="number"
                                  className="quantity-control-input b3"
                                />
                                <button
                                  data-ajax-cart-quantity-plus
                                  className="h-auto px-3"
                                >
                                  <span className="icon-plus text-[14px] text-[#99A8B2]"></span>
                                </button>
                              </div>
                            </AjaxCartQuantity>
                          )
                        )}
                        <a className={`item__remove b3`}>
                          <div
                            data-ajax-cart-request-button={`/cart/change?id=${line_item.key}&quantity=0`}
                          >
                            Remove
                          </div>
                        </a>
                      </div>

                      <div
                        className="text-[10px] errors"
                        style={{ padding: 0 }}
                        data-ajax-cart-errors={`${line_item.key}`}
                      ></div>
                    </div>

                    <div className={`flex justify-between items-center gap-4`}>
                      {tab === "clickAndCollect" && selectedStore && storeResults?.storesByLocation && (
                        !storeResults.storesByLocation[selectedStore.shopify_location_id]?.some(
                          (variant) => {
                            if (String(variant.shopify_variant_id) !== String(line_item.variant_id)) return false;
                            const requiredStock = (selectedStore as Store).clickandcollect_buffer === 1 ? 2 : 1;
                            return (variant.free_stock || 0) >= requiredStock;
                          }
                        ) && (
                          <p className="blabel text-[#D12727] mt-1">
                            Unavailable for pickup
                          </p>
                        )
                      )}

                      {tab === "homeDelivery" && props.globals.is_au && (
                        <InventoryDetail variantId={line_item.variant_id} />
                      )}

                      <div className={"flex flex-cols ml-auto"}>
                        <div className={"flex flex-col text-right"}>
                          <p className={`b-price`}>
                            {currencySymbol + line_item_price.toFixed(2)}
                          </p>

                          {(line_item.discounts?.length > 0 && line_item.original_line_price > line_item.line_price) && (
                            <p className="b-price line-through text-[#BBBBBD] text-[12px]">
                              {currencySymbol + (line_item.original_line_price / 100).toFixed(2)}
                            </p>
                          )}

                          {(line_item.discounts?.length === 0 && line_item.product?.compare_at_price) && (
                            <p className="b-price line-through text-[#BBBBBD] text-[12px]">
                              {currencySymbol + ((line_item.product.compare_at_price * line_item.quantity) / 100).toFixed(2)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
                <hr className="border-none h-[1px] bg-[#ECEEEF]" />
              </React.Fragment>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const CartTotals = ({ cart_subtotal, settings }) => {
  // @ts-ignore
  const currencySymbol = window.Alpine?.store("currency").getCurrencySymbol();

  return (
    <div className={"pt-3"}>
      <div className="uppercase minicart__totals">
        <Paragraph text={"Total"} type={"bc text-[#54565A] font-medium"} />
        <Paragraph
          text={`${currencySymbol}${cart_subtotal.toFixed(2)}`}
          type={"h3"}
        />
      </div>
      {settings.cart_info && (
        <Paragraph text={settings.cart_info} type={"minicart__info"} />
      )}
    </div>
  );
};

const CartNotes = ({ show = false }) => {
  return (
    <>
      <div className="cart_notes">
        <TextField
          type={"floating-input"}
          placeholder={"Enter notes for cart"}
          id={"cart_note"}
          value={undefined}
        />
      </div>
    </>
  );
};

const MiniCartActions = ({ show_cart, allVariantsAvailable, selectedStore, storeResults }) => {
  return (
    <div className={"minicart__actions"}>
      <form id="checkout" action="/checkout" method="post" className=""></form>
      <form id="cart" action="/cart" method="get" className=""></form>

      {show_cart && (
        <button className="btn-outline" type="submit" form={"cart"}>
          View Cart
        </button>
      )}

      <div className={"minicart__buttons"}>
        <button
          id="checkoutButton"
          className="btn-primary"
          type="submit"
          name="checkout"
          form="checkout"
          disabled={!allVariantsAvailable}
        >
          Proceed to Checkout
        </button>
        {!allVariantsAvailable &&
          selectedStore &&
          storeResults?.storesByLocation?.[selectedStore.shopify_location_id] && (
            <p className="blabel text-[#D12727] my-3">
              Not all items are available for Click & Collect at {selectedStore.name}. Select a different store, opt for other methods or remove unfulfillable items from your bag.
            </p>
        )}
      </div>
    </div>
  );
};

export default Minicart;